import React, { memo } from 'react';

import { Modal } from 'Components/Modal';
import { PurpleButton } from 'Components/Button';
import { TextBox } from 'Components/TextBox';
import { Spinner } from 'Components/Spinner';

import { areEqual } from 'Utils/equalityChecks';

import classes from './mergeProjectModal.module.css';

interface SourceProject {
  id: number;
  uid: string;
  alias: string;
  address?: any;
}

interface RoomMapping {
  source_room: string;
  target_room: string;
  is_new: boolean;
  room_type_name: string;
  level_name: string;
  photo_count: number;
}

interface PreviewData {
  counts: Record<string, number>;
  room_mappings: RoomMapping[];
  warnings: string[];
  template_data_not_moved?: Record<string, number>;
}

interface Props {
  isOpen: boolean;
  step: 'select' | 'preview' | 'merging' | 'done';
  sourceSearch: string;
  recentProjects: SourceProject[];
  sourceProjects: SourceProject[];
  dropdownLoading: boolean;
  dropdownOpen: boolean;
  selectedSource: SourceProject | null;
  preview: PreviewData | null;
  previewLoading: boolean;
  previewError: string;
  mergeError: string;
  confirmationString: string;
  targetUid: string;
  onSearchChange: (e: any) => void;
  onSearchFocus: () => void;
  onSelectSource: (project: SourceProject) => void;
  onClearSource: () => void;
  onBackClick: () => void;
  onMergeClick: (e: any) => void;
  onCloseClick: (e: any) => void;
  onChangeConfirmationString: (e: any) => void;
}

const MergeProjectModal = ({
  isOpen,
  step,
  sourceSearch,
  recentProjects,
  sourceProjects,
  dropdownLoading,
  dropdownOpen,
  selectedSource,
  preview,
  previewLoading,
  previewError,
  mergeError,
  confirmationString,
  targetUid,
  onSearchChange,
  onSearchFocus,
  onSelectSource,
  onClearSource,
  onBackClick,
  onMergeClick,
  onCloseClick,
  onChangeConfirmationString,
}: Props) => {
  const dropdownProjects = sourceSearch.length >= 2 ? sourceProjects : recentProjects;

  const renderSelect = () => (
    <div>
      <div className={classes.infoText}>
        Select the source project to merge into this one. All data from the source will be moved here, and the source
        project will be deleted.
      </div>

      {selectedSource ? (
        <div className={classes.selectedSource}>
          <div className={classes.selectedSourceInfo}>
            <span className={classes.selectedSourceUid}>{selectedSource.uid}</span>
            {selectedSource.alias && ` — ${selectedSource.alias}`}
          </div>
          <button type="button" className={classes.changeButton} onClick={onClearSource}>
            Change
          </button>
        </div>
      ) : (
        <div className={classes.searchWrapper}>
          <input
            type="text"
            className={classes.searchInput}
            placeholder="Search by project ID, name, address, or zip..."
            value={sourceSearch}
            onChange={onSearchChange}
            onFocus={onSearchFocus}
            autoFocus
          />
          {dropdownOpen && (
            <div className={classes.dropdown}>
              {dropdownLoading ? (
                <div className={classes.dropdownEmpty}>Loading projects...</div>
              ) : dropdownProjects.length > 0 ? (
                dropdownProjects.map((project) => (
                  <div
                    key={project.id}
                    className={classes.dropdownItem}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectSource(project)}
                    onKeyDown={(e) => e.key === 'Enter' && onSelectSource(project)}
                  >
                    <strong>{project.uid}</strong>
                    {project.alias && ` — ${project.alias}`}
                    {project.address && (
                      <span>
                        {' — '}
                        {project.address.address}
                        {project.address.city && `, ${project.address.city}`}
                      </span>
                    )}
                  </div>
                ))
              ) : (
                <div className={classes.dropdownEmpty}>
                  {sourceSearch.length >= 2 ? 'No projects found' : 'No recent projects found'}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {previewLoading && (
        <div className={classes.spinnerWrapper}>
          <Spinner loading />
          <div className={classes.spinnerText}>Loading preview...</div>
        </div>
      )}

      {previewError && <div className={classes.errorAlert}>{previewError}</div>}
    </div>
  );

  const renderPreview = () => {
    if (!preview || !selectedSource) return null;

    const { counts, room_mappings: roomMappings, warnings, template_data_not_moved: templateData } = preview;

    return (
      <div>
        <div className={classes.summaryHeader}>
          <span>{selectedSource.uid}</span>
          <span className={classes.arrow}>&rarr;</span>
          <span>{targetUid}</span>
        </div>

        {mergeError && <div className={classes.errorAlert}>{mergeError}</div>}

        <div className={classes.previewScroll}>
          {counts && Object.keys(counts).length > 0 && (
            <>
              <div className={classes.sectionTitle}>Data to be moved</div>
              <table className={classes.countsTable}>
                <tbody>
                  {Object.entries(counts).map(([key, value]) => (
                    <tr key={key}>
                      <td>{key.replace(/_/g, ' ')}</td>
                      <td>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {roomMappings && roomMappings.length > 0 && (
            <>
              <div className={classes.sectionTitle}>Room mappings</div>
              {roomMappings.map((mapping, index) => (
                <div key={`${mapping.source_room}-${index}`} className={classes.roomMapping}>
                  <span>
                    {mapping.source_room} &rarr; {mapping.target_room}
                    {mapping.is_new && <span className={classes.newBadge}>new</span>}
                  </span>
                  <span>{mapping.photo_count} photos</span>
                </div>
              ))}
            </>
          )}

          {warnings && warnings.length > 0 && (
            <div className={classes.warningSection}>
              {warnings.map((warning, index) => (
                <div key={`warning-${index}`} className={classes.warning}>
                  {warning}
                </div>
              ))}
            </div>
          )}

          {templateData && Object.keys(templateData).length > 0 && (
            <div className={classes.infoNote}>
              <strong>Not moved:</strong> Template data (
              {Object.entries(templateData)
                .map(([key, value]) => `${value} ${key.replace(/_/g, ' ')}`)
                .join(', ')}
              ) will remain with the source project.
            </div>
          )}
        </div>

        <div className={classes.confirmSection}>
          <div className={classes.confirmLabel}>
            Type the source project ID <strong>{selectedSource.uid}</strong> to confirm:
          </div>
          <TextBox
            name="merge-confirmation"
            type="text"
            ariaLabel=""
            className={classes.searchInput}
            value={confirmationString}
            onChange={onChangeConfirmationString}
          />
        </div>

        <div className={classes.buttonRow}>
          <PurpleButton className={classes.button} onClick={onBackClick}>
            Back
          </PurpleButton>
          <PurpleButton
            useDeleteColor
            className={classes.button}
            onClick={onMergeClick}
            disabled={confirmationString !== selectedSource.uid}
          >
            Merge Projects
          </PurpleButton>
        </div>
      </div>
    );
  };

  const renderMerging = () => (
    <div className={classes.spinnerWrapper}>
      <Spinner loading />
      <div className={classes.spinnerText}>Merging projects...</div>
    </div>
  );

  const renderDone = () => (
    <div className={classes.successWrapper}>
      <div className={classes.successText}>Projects merged successfully!</div>
      <PurpleButton className={classes.button} onClick={onCloseClick}>
        Close
      </PurpleButton>
    </div>
  );

  return (
    <Modal
      id="project-merge-modal"
      classes={classes}
      title="Merge Project Into This Project"
      isOpen={isOpen}
      modalHeader
      closeButtonText="Cancel"
      dataBsBackdrop="static"
      dataBsKeyboard="false"
      modalCloseClick={onCloseClick}
    >
      {step === 'select' && renderSelect()}
      {step === 'preview' && renderPreview()}
      {step === 'merging' && renderMerging()}
      {step === 'done' && renderDone()}
    </Modal>
  );
};

const MergeProjectModalMemo = memo(MergeProjectModal, areEqual);

export { MergeProjectModalMemo as MergeProjectModal };
